import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { NotificationBell } from './NotificationBell';

export const Navbar: React.FC = () => {
  const { currentUser, isAuthenticated, signOut } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    setMobileOpen(false);
    navigate('/');
  };

  const close = () => setMobileOpen(false);

  const desktopLink = ({ isActive }: { isActive: boolean }) =>
    `text-sm font-medium transition-colors ${isActive ? 'text-green-700' : 'text-gray-600 hover:text-gray-900'}`;

  const mobileLink = ({ isActive }: { isActive: boolean }) =>
    `block px-4 py-3 text-base font-medium transition-colors ${isActive ? 'text-green-700 bg-green-50' : 'text-gray-700 hover:bg-gray-50'}`;

  const canCreate = currentUser?.roles.includes('Teacher') ||
    currentUser?.roles.includes('Organizer') ||
    currentUser?.roles.includes('Admin');

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
        <Link to="/" className="text-xl font-bold text-green-700 hover:text-green-800" onClick={close}>
          Pop-Up Yoga
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-6">
          <NavLink to="/about" className={desktopLink}>About</NavLink>
          <NavLink to="/classes" className={desktopLink}>Classes</NavLink>
          <NavLink to="/contact" className={desktopLink}>Contact</NavLink>

          {isAuthenticated ? (
            <>
              <NavLink to="/dashboard" className={desktopLink}>Dashboard</NavLink>
              {canCreate && <NavLink to="/classes/create" className={desktopLink}>Create Class</NavLink>}
              <NavLink to="/profile" className={desktopLink}>Profile</NavLink>
              {currentUser?.roles.includes('Admin') && (
                <NavLink to="/admin" className={desktopLink}>Admin</NavLink>
              )}
              <NotificationBell />
              <button
                onClick={handleSignOut}
                className="text-sm font-medium text-red-500 hover:text-red-700 transition-colors"
              >
                Sign Out
              </button>
            </>
          ) : (
            <Link
              to="/auth"
              className="text-sm font-medium bg-green-700 hover:bg-green-800 text-white px-4 py-1.5 rounded-md transition-colors"
            >
              Sign In
            </Link>
          )}
        </div>

        {/* Mobile: bell + hamburger */}
        <div className="md:hidden flex items-center gap-1">
          {isAuthenticated && <NotificationBell />}
          <button
            className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            onClick={() => setMobileOpen(o => !o)}
            aria-label="Toggle menu"
          >
          {mobileOpen ? (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white divide-y divide-gray-100">
          <div>
            <NavLink to="/about" className={mobileLink} onClick={close}>About</NavLink>
            <NavLink to="/classes" className={mobileLink} onClick={close}>Classes</NavLink>
            <NavLink to="/contact" className={mobileLink} onClick={close}>Contact</NavLink>
          </div>

          <div>
            {isAuthenticated ? (
              <>
                <NavLink to="/dashboard" className={mobileLink} onClick={close}>Dashboard</NavLink>
                {canCreate && (
                  <NavLink to="/classes/create" className={mobileLink} onClick={close}>Create Class</NavLink>
                )}
                <NavLink to="/profile" className={mobileLink} onClick={close}>Profile</NavLink>
                {currentUser?.roles.includes('Admin') && (
                  <NavLink to="/admin" className={mobileLink} onClick={close}>Admin</NavLink>
                )}
                <button
                  onClick={handleSignOut}
                  className="block w-full text-left px-4 py-3 text-base font-medium text-red-500 hover:bg-red-50"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <Link
                to="/auth"
                className="block px-4 py-3 text-base font-medium text-green-700 hover:bg-green-50"
                onClick={close}
              >
                Sign In
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};
